import React from 'react';
import type { OperationProgressModel } from './operationProgress.types';
import { isOperationProgressBusy } from './operationProgressPresentation';
import { isOperationProgressWaitingState } from './operationProgressUtils';

interface OperationProgressTiming {
  readonly activeElapsedMs: number | null;
  readonly busy: boolean;
  readonly elapsedMs: number | null;
  readonly lastUpdateAgeMs: number;
  readonly terminal: boolean;
  readonly waitElapsedMs: number | null;
  readonly waiting: boolean;
}

interface LocalClock {
  readonly nowMs: number;
  readonly operationIdentity: string;
  readonly operationStartedAtMs: number;
  readonly receivedAtMs: number;
  readonly snapshotKey: string;
}

const getPrimitiveSnapshotKey = (model: OperationProgressModel): string =>
  JSON.stringify([
    model.operationId,
    model.title,
    model.message,
    model.detail,
    model.status,
    model.phase,
    model.state,
    model.functionName,
    model.currentItem,
    model.waitReason,
    model.waitElapsedMs,
    model.activeElapsedMs,
    model.processed,
    model.total,
    model.percent,
    model.elapsedMs,
    model.etaMs,
    model.cacheStatus,
    model.resultReady,
    model.error,
    model.stepLabel,
    model.etaLabel,
    model.locked,
  ]) ?? '';

export const isOperationProgressTerminal = (model: OperationProgressModel): boolean =>
  model.resultReady === true || model.status === 'complete' || model.status === 'cached';

export const useOperationProgressTiming = (
  model: OperationProgressModel
): OperationProgressTiming => {
  const snapshotKey = getPrimitiveSnapshotKey(model);
  const operationIdentity = model.operationId ?? model.title;
  const renderTimeMs = Date.now();
  const [clock, setClock] = React.useState<LocalClock>(() => ({
    nowMs: renderTimeMs,
    operationIdentity,
    operationStartedAtMs: renderTimeMs,
    receivedAtMs: renderTimeMs,
    snapshotKey,
  }));
  const terminal = isOperationProgressTerminal(model);
  const waitingState = isOperationProgressWaitingState(model.state);
  const waiting = !terminal && (model.status === 'waiting' || waitingState);
  const busy = !terminal && (
    isOperationProgressBusy(model.status) || waiting || model.state === 'completing'
  );
  const currentClock = clock.operationIdentity !== operationIdentity
    ? {
        nowMs: renderTimeMs,
        operationIdentity,
        operationStartedAtMs: renderTimeMs,
        receivedAtMs: renderTimeMs,
        snapshotKey,
      }
    : clock.snapshotKey === snapshotKey
      ? clock
      : { ...clock, nowMs: renderTimeMs, receivedAtMs: renderTimeMs, snapshotKey };

  React.useEffect(() => {
    setClock((current) => {
      const receivedAtMs = Date.now();
      if (current.operationIdentity !== operationIdentity) {
        return {
          nowMs: receivedAtMs,
          operationIdentity,
          operationStartedAtMs: receivedAtMs,
          receivedAtMs,
          snapshotKey,
        };
      }

      if (current.snapshotKey === snapshotKey) {
        return current;
      }

      return { ...current, nowMs: receivedAtMs, receivedAtMs, snapshotKey };
    });
  }, [operationIdentity, snapshotKey]);

  React.useEffect(() => {
    if (!busy) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setClock((current) => current.operationIdentity === operationIdentity
        && current.snapshotKey === snapshotKey
        ? { ...current, nowMs: Date.now() }
        : current);
    }, 1_000);
    return () => window.clearInterval(intervalId);
  }, [busy, operationIdentity, snapshotKey]);

  const snapshotAgeMs = Math.max(0, currentClock.nowMs - currentClock.receivedAtMs);
  const operationElapsedMs = Math.max(
    0,
    currentClock.nowMs - currentClock.operationStartedAtMs
  );
  const activeState = model.state === 'running'
    || model.state === 'completing'
    || (model.state === undefined && model.status === 'running');
  const waitingTime = waitingState || (model.state === undefined && model.status === 'waiting');

  return {
    activeElapsedMs: model.activeElapsedMs === null || model.activeElapsedMs === undefined
      ? null
      : model.activeElapsedMs + (busy && activeState ? snapshotAgeMs : 0),
    busy,
    elapsedMs: model.elapsedMs === null || model.elapsedMs === undefined
      ? (busy ? operationElapsedMs : null)
      : model.elapsedMs + (busy ? snapshotAgeMs : 0),
    lastUpdateAgeMs: terminal ? 0 : snapshotAgeMs,
    terminal,
    waitElapsedMs: model.waitElapsedMs === null || model.waitElapsedMs === undefined
      ? null
      : model.waitElapsedMs + (busy && waitingTime ? snapshotAgeMs : 0),
    waiting,
  };
};
